import React, { useCallback, useEffect, useState } from 'react';
import { C, LAYOUT } from '../../shared/design-tokens.js';
import { apiFetch, apiWrite } from '../../lib/api-client.js';
import { issueStatusPresentation } from './bug-trap-ui.js';

interface PortalIssue {
  id: string;
  status: string;
  reporter_name?: string;
  reporter_email: string;
  created_at?: string;
  which_page: string;
  frequency: string;
  what_doing: string;
  what_happened: string;
  admin_notes?: string;
}

interface IssuesResponse {
  issues?: PortalIssue[];
  error?: string;
}

interface IssueUpdateResponse {
  id?: string;
  status?: string;
  admin_notes?: string;
  error?: string;
}

type StatusFilter = 'all' | 'open' | 'fixed';

const FILTER_OPTIONS: Array<{ value: StatusFilter; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'open', label: 'Open' },
  { value: 'fixed', label: 'Fixed' },
];

function matchesFilter(issue: PortalIssue, filter: StatusFilter): boolean {
  if (filter === 'all') return true;
  if (filter === 'open') return issue.status === 'open' || issue.status === 'acknowledged';
  return issue.status === 'fixed' || issue.status === 'wont_fix';
}

const STATUS_ACTIONS: Array<{ value: string; label: string }> = [
  { value: 'open', label: 'Open' },
  { value: 'acknowledged', label: 'Acknowledge' },
  { value: 'fixed', label: 'Mark fixed' },
  { value: 'wont_fix', label: "Won't fix" },
];

export default function BugTrapIssueList() {
  const [issues, setIssues] = useState<PortalIssue[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasAccess, setHasAccess] = useState(true);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [updatingId, setUpdatingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiFetch<IssuesResponse>('/api/portal-issues');
      if (data?.error) {
        if (data.error.toLowerCase().includes('permission')) { setHasAccess(false); }
        else { setError(data.error); }
        setIssues([]);
      } else {
        setIssues(data.issues || []);
        setHasAccess(true);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load issues');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const visibleIssues = issues.filter((issue) => matchesFilter(issue, statusFilter));

  async function updateIssueStatus(issueId: string, status: string) {
    setUpdatingId(issueId);
    setError(null);
    try {
      const { ok, data } = await apiWrite<IssueUpdateResponse>(`/api/portal-issues/${issueId}`, 'PATCH', { status });
      if (!ok || data?.error) {
        setError(data?.error || 'Failed to update issue status');
        return;
      }
      setIssues((current) => current.map((issue) => (
        issue.id === issueId ? { ...issue, status: data?.status || status, admin_notes: data?.admin_notes ?? issue.admin_notes } : issue
      )));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update issue status');
    } finally {
      setUpdatingId(null);
    }
  }

  if (!hasAccess) return null;

  return (
    <div style={LAYOUT.subPanel}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
        <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.gold }}>
          Reported issues
        </div>
        {!loading && <div style={{ color: C.textMuted, fontSize: 12 }}>{visibleIssues.length} of {issues.length} report{issues.length !== 1 ? 's' : ''}</div>}
      </div>

      {!loading && !error && issues.length > 0 && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
          {FILTER_OPTIONS.map((option) => {
            const active = statusFilter === option.value;
            return (
              <button
                key={option.value}
                type="button"
                onClick={() => setStatusFilter(option.value)}
                style={{
                  padding: '6px 12px',
                  borderRadius: 999,
                  border: `1px solid ${active ? C.gold : C.borderSoft}`,
                  background: active ? 'rgba(201,162,39,0.12)' : 'transparent',
                  color: active ? C.gold : C.textMuted,
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                {option.label}
              </button>
            );
          })}
        </div>
      )}

      {loading && <div style={{ color: C.textMuted, fontSize: 12 }}>Loading reports...</div>}
      {error && <div style={{ color: C.red, fontSize: 12 }}>{error}</div>}

      {!loading && !error && issues.length === 0 && (
        <div style={{ color: C.green, fontSize: 13 }}>No bug reports yet</div>
      )}

      {!loading && !error && issues.length > 0 && visibleIssues.length === 0 && (
        <div style={{ color: C.textMuted, fontSize: 13 }}>No {statusFilter} reports in the current list.</div>
      )}

      {!loading && !error && visibleIssues.length > 0 && (
        <div style={{ display: 'grid', gap: 10 }}>
          {visibleIssues.map((issue) => {
            const status = issueStatusPresentation(issue.status);
            return (
              <div key={issue.id} style={{ padding: 12, borderRadius: 10, background: C.panel, border: `1px solid ${C.borderSoft}` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 6 }}>
                  <div style={{ color: C.text, fontSize: 13, fontWeight: 600 }}>{issue.reporter_name || issue.reporter_email}</div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <span style={{ fontSize: 11, padding: '3px 10px', borderRadius: 999, color: status.color, background: status.background }}>{status.label}</span>
                    <span style={{ color: C.textMuted, fontSize: 11 }}>{String(issue.created_at || '').slice(0, 10)}</span>
                  </div>
                </div>
                <div style={{ color: C.textMuted, fontSize: 12, marginBottom: 4 }}>Page: <span style={{ color: C.text }}>{issue.which_page}</span> · Frequency: <span style={{ color: C.text }}>{issue.frequency}</span></div>
                <div style={{ color: C.textMuted, fontSize: 12, marginBottom: 4 }}>Doing: <span style={{ color: C.text }}>{issue.what_doing}</span></div>
                <div style={{ color: C.textMuted, fontSize: 12 }}>Issue: <span style={{ color: C.text }}>{issue.what_happened}</span></div>
                {issue.admin_notes && (
                  <div style={{ color: C.gold, fontSize: 12, marginTop: 6, borderTop: `1px solid ${C.borderSoft}`, paddingTop: 6 }}>
                    Admin notes: {issue.admin_notes}
                  </div>
                )}
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
                  {STATUS_ACTIONS.map((action) => {
                    const active = issue.status === action.value;
                    return (
                      <button
                        key={action.value}
                        type="button"
                        onClick={() => updateIssueStatus(issue.id, action.value)}
                        disabled={active || updatingId === issue.id}
                        style={{
                          padding: '6px 10px',
                          borderRadius: 8,
                          border: `1px solid ${active ? C.gold : C.borderSoft}`,
                          background: active ? 'rgba(201,162,39,0.12)' : 'transparent',
                          color: active ? C.gold : C.textMuted,
                          fontSize: 11,
                          fontWeight: 600,
                          cursor: active || updatingId === issue.id ? 'not-allowed' : 'pointer',
                          opacity: updatingId === issue.id && !active ? 0.6 : 1,
                        }}
                      >
                        {active ? `${action.label} ✓` : action.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
